import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import contactsService, {
  SupplierContact,
  SupplierContactInput,
  SupplierContactRole
} from '../../services/supplierCrm/contactsService';
import type { Supplier } from '../../services/supplierCrm/suppliersService';

const ROLE_OPTIONS: Array<{ value: SupplierContactRole; label: string }> = [
  { value: 'account_manager', label: 'Account Manager' },
  { value: 'credit_control', label: 'Credit Control' },
  { value: 'logistics', label: 'Logistics' },
  { value: 'executive', label: 'Executive' },
  { value: 'operations', label: 'Operations' },
  { value: 'other', label: 'Other' }
];

interface SupplierContactsDrawerProps {
  isOpen: boolean;
  supplier: Supplier | null;
  organizationId: string | null;
  onClose: () => void;
  onContactsChanged?: () => void;
}

const SupplierContactsDrawer: React.FC<SupplierContactsDrawerProps> = ({
  isOpen,
  supplier,
  organizationId,
  onClose,
  onContactsChanged
}) => {
  const [contacts, setContacts] = useState<SupplierContact[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formState, setFormState] = useState<SupplierContactInput | null>(null);
  const [editingContactId, setEditingContactId] = useState<string | null>(null);

  const resetForm = useCallback(() => {
    setFormState(null);
    setEditingContactId(null);
  }, []);

  const fetchContacts = useCallback(async () => {
    if (!organizationId || !supplier) return;

    setLoading(true);
    setError(null);

    try {
      const { data } = await contactsService.list({
        organization_id: organizationId,
        supplier_id: supplier.id,
        limit: 50,
        offset: 0
      });
      setContacts(data || []);
    } catch (err: any) {
      const message = err?.message || 'Failed to load supplier contacts';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [organizationId, supplier]);

  useEffect(() => {
    if (isOpen) {
      void fetchContacts();
      resetForm();
    }
  }, [fetchContacts, isOpen, resetForm]);

  const startCreate = useCallback(() => {
    if (!supplier || !organizationId) {
      toast.error('Select a supplier first');
      return;
    }

    setFormState({
      organization_id: organizationId,
      supplier_id: supplier.id,
      full_name: '',
      role: 'account_manager',
      is_primary: false
    });
    setEditingContactId(null);
  }, [organizationId, supplier]);

  const startEdit = useCallback((contact: SupplierContact) => {
    setFormState({
      organization_id: contact.organization_id,
      supplier_id: contact.supplier_id,
      full_name: contact.full_name,
      job_title: contact.job_title,
      role: contact.role,
      email: contact.email,
      phone: contact.phone,
      notes: contact.notes,
      is_primary: contact.is_primary
    });
    setEditingContactId(contact.id);
  }, []);

  const cancelForm = useCallback(() => {
    resetForm();
  }, [resetForm]);

  const handleFormChange = useCallback(<K extends keyof SupplierContactInput>(key: K, value: SupplierContactInput[K]) => {
    setFormState((prev) => (prev ? { ...prev, [key]: value } : prev));
  }, []);

  const submitForm = useCallback(async () => {
    if (!formState) return;

    if (!formState.full_name.trim()) {
      toast.error('Contact name is required');
      return;
    }

    if (!formState.organization_id || !formState.supplier_id) {
      toast.error('Organization and supplier are required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (editingContactId) {
        await contactsService.update(editingContactId, formState);
        toast.success('Contact updated');
      } else {
        await contactsService.create(formState);
        toast.success('Contact added');
      }
      await fetchContacts();
      onContactsChanged?.();
      resetForm();
    } catch (err: any) {
      const message = err?.message || 'Failed to save contact';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [editingContactId, fetchContacts, formState, onContactsChanged, resetForm]);

  const handleDelete = useCallback(async (contact: SupplierContact) => {
    if (!organizationId) return;

    const confirmed = window.confirm(`Delete contact ${contact.full_name}?`);
    if (!confirmed) return;

    setLoading(true);
    setError(null);

    try {
      await contactsService.remove(contact.id, organizationId);
      toast.success('Contact deleted');
      await fetchContacts();
      onContactsChanged?.();
    } catch (err: any) {
      const message = err?.message || 'Failed to delete contact';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [fetchContacts, onContactsChanged, organizationId]);

  const contactList = useMemo(() => (
    <div className="space-y-4">
      {contacts.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-200 p-6 text-sm text-gray-500">
          {loading ? 'Loading contacts…' : 'No contacts yet. Add a primary point of contact for this supplier.'}
        </div>
      ) : (
        <ul className="space-y-3">
          {contacts.map((contact) => (
            <li key={contact.id} className="rounded-lg border border-gray-200 p-4 shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-gray-900">
                    {contact.full_name}
                    {contact.is_primary ? (
                      <span className="ml-2 inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                        Primary
                      </span>
                    ) : null}
                  </div>
                  <div className="text-xs text-gray-500">{contact.job_title || 'Role not set'}</div>
                  <div className="mt-2 text-xs text-gray-600">
                    <span className="font-medium">Role:</span> {ROLE_OPTIONS.find((opt) => opt.value === contact.role)?.label || contact.role}
                  </div>
                  <div className="mt-2 space-y-1 text-xs text-gray-600">
                    <div>{contact.email || 'No email provided'}</div>
                    <div>{contact.phone || 'No phone provided'}</div>
                  </div>
                  {contact.notes ? (
                    <div className="mt-2 text-xs text-gray-500">{contact.notes}</div>
                  ) : null}
                </div>
                <div className="flex flex-col gap-2">
                  <button
                    type="button"
                    className="inline-flex items-center justify-center rounded-md border border-gray-300 px-3 py-1 text-xs font-semibold text-gray-700 transition hover:bg-gray-50"
                    onClick={() => startEdit(contact)}
                    disabled={loading}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="inline-flex items-center justify-center rounded-md border border-red-200 px-3 py-1 text-xs font-semibold text-red-600 transition hover:bg-red-50"
                    onClick={() => void handleDelete(contact)}
                    disabled={loading}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  ), [contacts, handleDelete, loading, startEdit]);

  const form = useMemo(() => (
    <div className="space-y-4 rounded-lg border border-primary/20 bg-primary/5 p-4">
      <div>
        <label className="text-xs font-semibold uppercase tracking-wide text-primary">Full name</label>
        <input
          className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          value={formState?.full_name ?? ''}
          onChange={(event) => handleFormChange('full_name', event.target.value)}
          placeholder="Contact name"
        />
      </div>
      <div>
        <label className="text-xs font-semibold uppercase tracking-wide text-primary">Job title</label>
        <input
          className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          value={formState?.job_title ?? ''}
          onChange={(event) => handleFormChange('job_title', event.target.value)}
          placeholder="Financial Director, Operations Lead, etc."
        />
      </div>
      <div>
        <label className="text-xs font-semibold uppercase tracking-wide text-primary">Role</label>
        <select
          className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          value={formState?.role ?? 'account_manager'}
          onChange={(event) => handleFormChange('role', event.target.value as SupplierContactRole)}
        >
          {ROLE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wide text-primary">Email</label>
          <input
            className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            value={formState?.email ?? ''}
            onChange={(event) => handleFormChange('email', event.target.value)}
            placeholder="contact@example.com"
          />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-wide text-primary">Phone</label>
          <input
            className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            value={formState?.phone ?? ''}
            onChange={(event) => handleFormChange('phone', event.target.value)}
            placeholder="+44 1234 567890"
          />
        </div>
      </div>
      <div>
        <label className="text-xs font-semibold uppercase tracking-wide text-primary">Notes</label>
        <textarea
          className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          rows={3}
          value={formState?.notes ?? ''}
          onChange={(event) => handleFormChange('notes', event.target.value)}
          placeholder="Additional context for this contact"
        />
      </div>
      <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
        <input
          type="checkbox"
          className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary/40"
          checked={formState?.is_primary ?? false}
          onChange={(event) => handleFormChange('is_primary', event.target.checked)}
        />
        Set as primary contact for this supplier
      </label>
      <div className="flex items-center justify-end gap-3">
        <button
          type="button"
          className="rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-600 transition hover:bg-gray-50"
          onClick={cancelForm}
          disabled={loading}
        >
          Cancel
        </button>
        <button
          type="button"
          className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-primary/90"
          onClick={() => void submitForm()}
          disabled={loading}
        >
          {editingContactId ? 'Save changes' : 'Add contact'}
        </button>
      </div>
      {error && <div className="text-xs text-red-600">{error}</div>}
    </div>
  ), [cancelForm, editingContactId, error, formState, handleFormChange, loading, submitForm]);

  if (!isOpen || !supplier) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="absolute inset-0 bg-gray-500/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative ml-auto flex h-full w-full max-w-xl flex-col bg-white shadow-xl">
        <div className="flex items-start justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{supplier.name}</h2>
            <p className="text-sm text-gray-500">Manage contacts for this supplier.</p>
          </div>
          <button
            type="button"
            className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-2"
            onClick={onClose}
          >
            <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 space-y-6 overflow-y-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-800">Contacts</h3>
            <button
              type="button"
              className="inline-flex items-center rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-primary/90"
              onClick={startCreate}
              disabled={loading || !supplier}
            >
              Add contact
            </button>
          </div>
          {contactList}
          {formState ? form : null}
        </div>
      </div>
    </div>
  );
};

export default SupplierContactsDrawer;
